import React, { useState } from 'react';
import { saveSite, saveStoryline, suggestLinks, suggestSite } from '../api/gameBuilderAPI';
import SiteCard from './SiteCard';

/**
 * The plan's games, always linked (backend plan.chain_games: each game joins the end of the chain as it's built - no
 * storyline to create). Between each pair, a LINK the creator picks: 🕐 later (a time skip, same place) or 🚶 another
 * room. The first move to another room draws the floor plan (one small AI call - backend plan.suggest_site), shown
 * below for the creator to edit; players then choose the room on it (SitePlayer). Each change is saved at once.
 */
const pairKey = (a, b) => `${a}>${b}`;

export default function ChainCard({ plan, onSaved, onPlay }) {
  const story = plan.storyline;
  const names = Object.fromEntries(plan.missions.map((m) => [m.id, m.name]));
  const built = (id) => !!plan.status?.[id]?.game;
  const [labels, setLabels] = useState({});          // link index -> what the creator is typing (saved on leaving it)
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const run = async (label, fn) => {
    setBusy(label);
    setError('');
    try {
      return await fn();
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy('');
    }
  };

  const save = async (order, links) => {
    const out = await run('Saving…', () => saveStoryline(plan.id, order, links));
    if (out) { onSaved({ storyline: out.storyline }); setLabels({}); }
    return out;
  };

  // A new order keeps every link the creator set for the same pair; a new pair gets code's suggestion.
  const move = async (i, d) => {
    const order = [...story.missions];
    [order[i], order[i + d]] = [order[i + d], order[i]];
    const kept = Object.fromEntries(story.missions.slice(1).map((b, k) => [pairKey(story.missions[k], b), story.links[k]]));
    const fresh = await run('Suggesting links…', () => suggestLinks(plan.id, order));
    if (!fresh) return;
    save(order, order.slice(1).map((b, k) => kept[pairKey(order[k], b)] || fresh.links[k]));
  };

  const setLink = async (i, patch) => {
    const links = story.links.map((l, k) => (k === i ? { ...l, ...patch } : l));
    const saved = await save(story.missions, links);
    // a move to another room plays on the floor plan: the first one draws it
    if (saved && patch.type === 'place' && !plan.site) {
      const out = await run('Drawing the floor plan… (one small AI call)', async () => saveSite(plan.id, (await suggestSite(plan.id)).site));
      if (out) onSaved({ site: out.site });
    }
  };

  if (!story || story.missions.length === 0) {
    return (
      <section className="gb-card gb-story">
        <h2 className="gb-story-title">Game chain</h2>
        <p className="gb-muted">Paint a mission's game and it starts the chain; each game after it joins the end.</p>
      </section>
    );
  }

  const playable = story.missions.filter(built);
  const moves = story.links.some((l) => l.type === 'place');
  return (
    <section className="gb-card gb-story">
      <div className="gb-plan-head">
        <h2 className="gb-story-title">Game chain</h2>
        <button type="button" className="gb-btn gb-btn-sm" disabled={playable.length === 0 || !!busy} onClick={onPlay}>▶ Play the chain</button>
      </div>
      <ol className="gb-chain">
        {story.missions.map((id, i) => (
          <React.Fragment key={id}>
            <li className={`gb-chain-mission is-editing ${built(id) ? '' : 'is-unbuilt'}`}>
              <span className="gb-mid">{id}</span> <span className="gb-chain-name">{names[id]}</span>
              {!built(id) && <span className="gb-small gb-muted"> · being rebuilt - skipped until painted</span>}
              <span className="gb-chain-tools">
                <button type="button" className="gb-icon-btn" aria-label={`Move ${names[id]} earlier`} disabled={i === 0 || !!busy} onClick={() => move(i, -1)}>↑</button>
                <button type="button" className="gb-icon-btn" aria-label={`Move ${names[id]} later`}
                  disabled={i === story.missions.length - 1 || !!busy} onClick={() => move(i, 1)}>↓</button>
              </span>
            </li>
            {i < story.links.length && (
              <li className="gb-chain-link is-editing">
                <span className="gb-seg" role="radiogroup" aria-label={`How ${names[story.missions[i + 1]]} begins`}>
                  {[['time', '🕐 Later'], ['place', '🚶 Another room']].map(([type, label]) => (
                    <button key={type} type="button" role="radio" aria-checked={story.links[i].type === type} disabled={!!busy}
                      className={story.links[i].type === type ? 'is-on' : ''}
                      onClick={() => (story.links[i].type !== type || (type === 'place' && !plan.site)) && setLink(i, { type })}>{label}</button>
                  ))}
                </span>
                <input className="gb-link-label" maxLength={60} aria-label="What the player sees" disabled={!!busy}
                  value={labels[i] ?? story.links[i].label ?? ''}
                  placeholder={story.links[i].type === 'place' ? 'To the loading dock' : 'Two hours later'}
                  onChange={(e) => setLabels((x) => ({ ...x, [i]: e.target.value }))}
                  onBlur={() => labels[i] !== undefined && labels[i] !== story.links[i].label && setLink(i, { label: labels[i] })} />
              </li>
            )}
          </React.Fragment>
        ))}
      </ol>
      {moves && plan.site && <SiteCard plan={plan} embedded onSaved={(site) => onSaved({ site })} />}
      {moves && !plan.site && !busy && <p className="gb-small gb-muted">The floor plan couldn't be drawn - pick 🚶 Another room again to retry.</p>}
      {busy && <p className="gb-progress"><span className="gb-spinner" /> {busy}</p>}
      {error && <div className="gb-error" role="alert">{error}</div>}
    </section>
  );
}
